import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../Modal';
import Spinner from '../../Spinner';
import { RSAlert, RSButton } from 'reactsymbols-kit';
import { saveAs } from 'file-saver';
import { Parser as Json2csvParser } from '@json2csv/plainjs';
import Styles from '../../../styles/colors';
import type { IOET } from '../../../stores/model/uimodel/oet';
import type { FieldInfo } from '@json2csv/plainjs';

/** A single OET data row (object column name -> cell value). */
type OETRowData = Record<string, string>;

/**
 * A json2csv field spec: either a plain column-name string (the OET object
 * columns) or a `FieldInfo` object (the leading event column). The literal seed
 * array below mixes the two, so it is typed as this union element to let
 * `.concat(oet.columnNames)` (a `string[]`) widen cleanly.
 */
type OETField = string | FieldInfo<OETRowData, unknown>;

/** Props for the inner {@link ExportOETCsv} content and its modal wrapper. */
interface ExportOETCsvModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** The OET to export. */
  oet: IOET;
  /** The model name used for the saved file. */
  modelName: string;
  /** Close the modal. */
  onRequestClose: () => void;
}

/**
 * Export the OET as a CSV
 */
function ExportOETCsv({ oet, modelName, onRequestClose }: ExportOETCsvModalProps) {
  const [exporting, setExporting] = useState(true);
  const [error, setError] = useState(false);

  // Keep the latest props in refs so exportCSV (used by the mount effect and
  // the retry button) always sees fresh values without re-registering.
  const propsRef = useRef({ oet, modelName, onRequestClose });
  propsRef.current = { oet, modelName, onRequestClose };

  // save the csv
  const saveCSV = (csv: string) => {
    const { modelName } = propsRef.current;
    const blob = new Blob([csv], { type: 'application/csv' });
    saveAs(blob, `${modelName}_oet.csv`);
  };

  /**
   * Generate the csv using json2csv:
   * https://github.com/zemirco/json2csv
   */
  const generateCSV = (): Promise<string> => {
    const { oet } = propsRef.current;
    return new Promise((resolve, reject) => {
      // force into a new task to give the browser a chance to render in between
      setTimeout(() => {
        try {
          const parser = new Json2csvParser<OETRowData, OETRowData>({
            fields: ([
              {
                label: ' ',
                value: 'event',
              },
            ] as OETField[]).concat(oet.columnNames),
            withBOM: true,
          });
          const csv = parser.parse(oet.json);
          resolve(csv);
        } catch (err) {
          reject(err);
        }
      }, 0);
    });
  };

  // generate and save the csv
  const exportCSV = () => {
    const { onRequestClose } = propsRef.current;
    setExporting(true);
    setError(false);
    generateCSV()
      .then((csv) => {
        saveCSV(csv);
        onRequestClose();
      })
      .catch(() => {
        setExporting(false);
        setError(true);
      });
  };

  // start exporting on component load (componentDidMount)
  useEffect(() => {
    exportCSV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // render either a spinner or an error
  const renderContent = () => {
    if (exporting) {
      return (
        <React.Fragment>
          <Spinner />
          Please be patient while we export your CSV-file.
        </React.Fragment>
      );
    } else if (error) {
      return (
        <React.Fragment>
          <RSAlert level="danger" visible={true}>
            <p>We couldn't export the OET as a CSV-file due to an unexpected error.</p>
          </RSAlert>
          <div className="button-wrapper">
            <RSButton
              type="submit"
              background={Styles.blueGradient}
              onClick={exportCSV}
              value="Retry"
            />
            <RSButton
              type="button"
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              onClick={onRequestClose}
              value="Close"
            />
          </div>
        </React.Fragment>
      );
    } else {
      return null;
    }
  };

  return <div className="export-csv-modal">{renderContent()}</div>;
}

// wrap in a Modal
const ExportOETCsvModal = (props: ExportOETCsvModalProps) => {
  return (
    <Modal isOpen={props.isOpen}>
      <ExportOETCsv {...props} />
    </Modal>
  );
};

export default ExportOETCsvModal;
